import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { UserPlus, Copy, KeyRound, X, Check, Loader2, ShieldOff, ShieldCheck, Phone } from "lucide-react";

const inputCls = "w-full rounded-xl bg-white border border-[#E4E0D6] focus:border-[#0B0D10] outline-none px-3.5 h-11 text-[13.5px] transition-colors";
const labelCls = "block text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#3A3D44]/60 mb-1.5";

function CredsCard({ creds, onClose }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`BackPocket sales login\nEmail: ${creds.email}\nPassword: ${creds.temporary_password}\nLogin: ${window.location.origin}/login`);
      toast.success("Credentials copied — send them to your staff member");
    } catch (_) {}
  };
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-3xl bg-[#0B0D10] text-white p-6 relative overflow-hidden grain mb-6" data-testid="staff-creds-card">
      <div className="absolute -top-12 -right-12 h-44 w-44 rounded-full bg-[#C8FF4D]/15 blur-3xl pointer-events-none" />
      <button onClick={onClose} className="absolute top-4 right-4 h-8 w-8 rounded-full grid place-items-center text-white/50 hover:text-white hover:bg-white/10 transition-colors" data-testid="staff-creds-close"><X size={14} /></button>
      <div className="relative">
        <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] font-bold bg-[#C8FF4D] text-[#0B0D10] rounded-full px-2.5 py-1 mb-3"><KeyRound size={11} strokeWidth={2.8} />Shown once</div>
        <h3 className="font-display font-extrabold text-[20px] tracking-[-0.02em]">{creds.name}'s login is ready.</h3>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl bg-white/[0.06] border border-white/12 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/50 font-semibold">Email</div>
            <div className="text-[14px] font-mono mt-1 truncate">{creds.email}</div>
          </div>
          <div className="rounded-xl bg-white/[0.06] border border-white/12 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/50 font-semibold">Temporary password</div>
            <div className="text-[14px] font-mono mt-1" data-testid="staff-temp-password">{creds.temporary_password}</div>
          </div>
        </div>
        <button onClick={copy} data-testid="staff-creds-copy"
          className="btn-lift mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#C8FF4D] text-[#0B0D10] text-[13px] font-bold">
          <Copy size={13} strokeWidth={2.4} />Copy credentials
        </button>
      </div>
    </motion.div>
  );
}

export default function AdminStaff() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/staff").then((r) => setRows(r.data.staff || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/staff", form);
      setCreds(data); setForm({ name: "", email: "" }); setOpen(false);
      toast.success(`${data.name} added to the team`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const toggle = async (u) => {
    try {
      await api.post(`/admin/staff/${u.id}/${u.active ? "deactivate" : "reactivate"}`);
      toast.success(u.active ? `${u.name} deactivated` : `${u.name} reactivated`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resetPw = async (u) => {
    if (!window.confirm(`Reset ${u.name}'s password? Their old one stops working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/staff/${u.id}/reset-password`);
      setCreds({ name: u.name, email: u.email, temporary_password: data.temporary_password });
      toast.success("New password generated");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-staff-page">
      <PageHeader title="Sales staff" subtitle="Logins for your cold-calling team. They get the Sales Desk — never financials or billing."
        action={<button data-testid="admin-staff-create-button" onClick={() => setOpen(true)}
          className="btn-lift bg-[#0B0D10] text-white rounded-full h-10 px-5 text-[13px] font-semibold inline-flex items-center gap-1.5 hover:bg-black transition-colors">
          <UserPlus size={14} strokeWidth={2.4} />Add staff member
        </button>} />

      {creds && <CredsCard creds={creds} onClose={() => setCreds(null)} />}

      {rows === null ? (
        <div className="text-[13px] text-[#3A3D44]/60 animate-pulse">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-[#E8E5DE] p-14 text-center bg-white/40" data-testid="staff-empty">
          <Phone size={22} className="mx-auto text-[#3A3D44]/40 mb-3" />
          <h3 className="font-display font-bold text-lg tracking-[-0.01em]">No sales staff yet.</h3>
          <p className="text-[13px] text-[#3A3D44]/60 mt-1.5 max-w-sm mx-auto">Add your first caller — they'll get a login that opens straight onto the Sales Desk.</p>
        </div>
      ) : (
        <div className="rounded-3xl bg-white border border-[#E8E5DE] overflow-hidden shadow-sm" data-testid="staff-list">
          <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1.6fr_0.7fr_0.7fr_0.6fr_auto] gap-4 px-5 py-3 bg-[#F4F1E9]/60 border-b border-[#E8E5DE] text-[10.5px] uppercase tracking-[0.14em] text-[#3A3D44]/60 font-semibold">
            <div>Staff member</div>
            <div className="hidden sm:block">Calls booked</div>
            <div className="hidden sm:block">Prospects</div>
            <div className="hidden sm:block">Status</div>
            <div className="text-right">Actions</div>
          </div>
          <ul className="divide-y divide-[#F0EDE5]">
            <AnimatePresence>
              {rows.map((u) => (
                <motion.li key={u.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="grid grid-cols-[1fr_auto] sm:grid-cols-[1.6fr_0.7fr_0.7fr_0.6fr_auto] gap-4 items-center px-5 py-4 hover:bg-[#FAF7F0] transition-colors" data-testid={`staff-row-${u.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`h-10 w-10 rounded-xl font-display font-extrabold grid place-items-center text-[12px] shrink-0 ${u.active ? "bg-[#0B0D10] text-[#C8FF4D]" : "bg-[#EFEBE1] text-[#3A3D44]/50"}`}>
                      {(u.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold text-[14px] truncate">{u.name}</div>
                      <div className="text-[12px] text-[#3A3D44]/60 truncate">{u.email}</div>
                    </div>
                  </div>
                  <div className="hidden sm:block font-display font-bold text-[15px] tabular-nums">{u.calls_booked}</div>
                  <div className="hidden sm:block font-display font-bold text-[15px] tabular-nums">{u.prospects_created}</div>
                  <div className="hidden sm:block">
                    <span className={`text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-0.5 rounded-full ${u.active ? "bg-[#C8FF4D] text-[#0B0D10]" : "bg-[#EFEBE1] text-[#3A3D44]/60"}`}>{u.active ? "Active" : "Deactivated"}</span>
                  </div>
                  <div className="flex items-center gap-1.5 justify-end">
                    <button data-testid={`staff-reset-pw-${u.id}`} onClick={() => resetPw(u)} title="Reset password"
                      className="h-9 w-9 rounded-full bg-white border border-[#E4E0D6] text-[#3A3D44]/70 grid place-items-center hover:border-[#0B0D10]/40 hover:text-[#0B0D10] transition-colors"><KeyRound size={13} /></button>
                    <button data-testid={`staff-toggle-${u.id}`} onClick={() => toggle(u)} title={u.active ? "Deactivate" : "Reactivate"}
                      className={`h-9 px-3.5 rounded-full text-[12px] font-semibold inline-flex items-center gap-1.5 transition-colors ${u.active ? "bg-white border border-[#E4E0D6] text-[#3A3D44] hover:border-red-300 hover:text-red-600" : "bg-[#C8FF4D] text-[#0B0D10]"}`}>
                      {u.active ? <><ShieldOff size={12} />Deactivate</> : <><ShieldCheck size={12} />Reactivate</>}
                    </button>
                  </div>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        </div>
      )}

      {/* Create modal */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 grid place-items-center px-4" data-testid="staff-create-modal">
            <div className="absolute inset-0 bg-[#0B0D10]/50 backdrop-blur-[2px]" onClick={() => !busy && setOpen(false)} />
            <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} transition={{ duration: 0.28 }}
              className="relative bg-white rounded-3xl p-7 w-full max-w-md shadow-2xl">
              <h3 className="font-display font-extrabold text-[20px] tracking-[-0.02em]">New sales staff member</h3>
              <p className="text-[13px] text-[#3A3D44]/60 mt-1">They'll get a temporary password to change on first login.</p>
              <form onSubmit={create} className="mt-5 space-y-4">
                <div>
                  <label className={labelCls}>Full name</label>
                  <input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Jordan Smith" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input data-testid="staff-email-input" type="email" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} placeholder="jordan@backpocketloyalty.com" className={inputCls} />
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="submit" disabled={busy || form.name.length < 2 || !form.email} data-testid="staff-create-submit"
                    className="btn-lift flex-1 h-11 rounded-full bg-[#0B0D10] text-white font-semibold text-[13.5px] inline-flex items-center justify-center gap-2 disabled:opacity-40">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={2.6} />}Create login
                  </button>
                  <button type="button" onClick={() => setOpen(false)} className="h-11 px-5 rounded-full bg-white border border-[#E4E0D6] text-[13.5px] font-medium text-[#3A3D44] hover:border-[#0B0D10]/40 transition-colors">Cancel</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
